/**
 * Client-side player name filtering
 *
 * Filters player results by matching search text against the backend-canonical
 * `Player.name` (SE-5469) plus `knownName`, `firstName` and `lastName` as
 * individual tokens (SE-6197). Used when the filterPlayers query is active
 * because the backend's PlayerAdvancedFilterInput has no search field.
 * @module features/scouting/utils/filterPlayersBySearchText
 */

import type { PlayerSearchPlayerFragment } from "@/generated/graphql";
import { normalizeForSearch } from "@/utils/normalizeForSearch";

/** Runs of whitespace, used to split a name or query into tokens. */
const WHITESPACE = /\s+/;

/**
 * Builds the normalized strings a query is matched against for one player.
 *
 * Mirrors the five player-name arms of the backend's search predicate
 * (SE-6196: first_name, last_name, known_name, `CONCAT(first, ' ', last)` and
 * `CONCAT_WS(' ', first, known_name, last)`), plus the backend-canonical
 * `name` retained from SE-5469. Both assembled forms are load-bearing:
 * knownName may be a nickname, a name particle or a duplicate of the full
 * name, so the three-part form is what matches a query containing the
 * known-name token ("Frenkie de Jong"), while the plain first+last form keeps
 * matching full-name queries where knownName is a particle nobody types.
 * @param player - Player whose names are being searched
 * @returns Normalized candidate strings; blank fields are omitted
 */
const buildNameCandidates = (
  player: PlayerSearchPlayerFragment
): readonly string[] => {
  const { name, knownName, firstName, lastName } = player;

  // Mirrors NULLIF(BTRIM(x), '') inside the backend's CONCAT_WS: blank and
  // null parts drop out entirely rather than leaving a doubled separator.
  const concatWsParts = [firstName, knownName, lastName]
    .map(part => part?.trim() ?? "")
    .filter(Boolean);

  return [
    name,
    firstName,
    lastName,
    // knownName is nullable: a null contributes no candidate, so it simply
    // never matches instead of throwing.
    knownName ?? "",
    `${firstName} ${lastName}`,
    concatWsParts.join(" "),
  ]
    .filter(Boolean)
    .map(normalizeForSearch);
};

/**
 * Decides whether a player's names satisfy the search query.
 * @param candidates - Normalized candidate strings from buildNameCandidates
 * @param query - Normalized full query string
 * @param queryTokens - Normalized, non-empty query tokens
 * @returns True when the player should remain in the filtered results
 */
const matchesQuery = (
  candidates: readonly string[],
  query: string,
  queryTokens: readonly string[]
): boolean => {
  // Backend parity: the whole query as a substring, matching SQL
  // LIKE '%term%'. Substring rather than prefix, so the client never drops a
  // row the backend's own predicate would have returned.
  if (candidates.some(candidate => candidate.includes(query))) {
    return true;
  }

  // Token rule: every query token must substring-match some name token. This
  // additionally covers full-name queries whose tokens are not adjacent in any
  // single candidate, e.g. "gabriel magalhaes" against a row stored as
  // "Gabriel dos Santos Magalhães".
  const nameTokens = candidates.flatMap(candidate =>
    candidate.split(WHITESPACE)
  );
  return queryTokens.every(queryToken =>
    nameTokens.some(nameToken => nameToken.includes(queryToken))
  );
};

/**
 * Filters players whose names match the search text, case- and
 * accent-insensitively. Returns the original array when searchText is empty or
 * whitespace only.
 * @param players - Player results from either searchPlayers or filterPlayers query
 * @param searchText - Debounced search input (already trimmed/lowercased by caller)
 * @returns Filtered subset of players matching the search text
 */
const filterPlayersBySearchText = (
  players: readonly PlayerSearchPlayerFragment[],
  searchText: string
): readonly PlayerSearchPlayerFragment[] => {
  const query = normalizeForSearch(searchText);
  const queryTokens = query.split(WHITESPACE).filter(Boolean);

  if (queryTokens.length === 0) {
    return players;
  }

  return players.filter(player =>
    matchesQuery(buildNameCandidates(player), query, queryTokens)
  );
};

export default filterPlayersBySearchText;
